import {
  AiAnswerOutcome,
  AiAnswerReason,
  AiAnswerSuggestion,
  AiMessageSteps
} from "__generated__/graphql"

export type ExplanationTone = "success" | "warning" | "error" | "neutral"

export const OUTCOME_LABELS: Record<
  AiAnswerOutcome,
  { label: string; tone: ExplanationTone }
> = {
  [AiAnswerOutcome.Answered]: { label: "Cevap verdi", tone: "success" },
  [AiAnswerOutcome.AnsweredAndHandedOff]: {
    label: "Cevap verdi ve temsilciye devretti",
    tone: "success"
  },
  [AiAnswerOutcome.HandedOff]: {
    label: "Cevap vermedi, temsilciye bıraktı",
    tone: "warning"
  },
  [AiAnswerOutcome.NotAnswered]: { label: "Cevap vermedi", tone: "warning" },
  [AiAnswerOutcome.MarkedSpam]: { label: "Spam işaretledi", tone: "neutral" },
  [AiAnswerOutcome.Failed]: { label: "Cevap üretemedi", tone: "error" },
  [AiAnswerOutcome.InProgress]: { label: "Run sürüyor", tone: "neutral" },
  [AiAnswerOutcome.NotRun]: { label: "Run bulunamadı", tone: "neutral" },
  [AiAnswerOutcome.Unknown]: {
    label: "v1 run — karar okunamıyor",
    tone: "neutral"
  }
}

/** Written for Qpien's own team: each line names the host rule or step behind the reason. */
export const REASON_LABELS: Record<
  AiAnswerReason,
  { title: string; detail: string }
> = {
  [AiAnswerReason.HandoffRequested]: {
    title: "Ajan devretmeye karar verdi",
    detail:
      "AGENT_OUTPUT.action = handoff (ya da clarify/boş cevap). Gerekçe ajanın kendi handoffReason / notes alanında."
  },
  [AiAnswerReason.ClarifyWithheld]: {
    title: "Soru sormak istedi, tek yanıt kanalı",
    detail:
      "action = clarify, yüzey tek yanıtlık (yorum / pazaryeri sorusu). Delivery gate soruyu tuttu ve sessizce devretti (SINGLE_REPLY_WITHHELD)."
  },
  [AiAnswerReason.EmptyReply]: {
    title: "Ajan boş cevap döndü",
    detail:
      "action = answer ama reply boş. Tek yanıt yüzeyinde EMPTY_REPLY_WITHHELD, sohbette devir mesajıyla devir."
  },
  [AiAnswerReason.NoReplyNeeded]: {
    title: "Ajan cevap gerekmediğine karar verdi",
    detail:
      "action = no_reply (teşekkür, onay vb.). Karta NO_REPLY sebebi yazıldı."
  },
  [AiAnswerReason.Spam]: {
    title: "Spam olarak işaretlendi",
    detail: "action = spam ve tenant'ın SPAM rehber kuralı var → MARK_SPAM."
  },
  [AiAnswerReason.SpamNotConfigured]: {
    title: "Spam dedi ama SPAM kuralı yok",
    detail:
      "action = spam, tenant'ta SPAM kategorisinde rehber kuralı yok. Gate işaretlemedi, cevap da gitmedi (SPAM_NOT_CONFIGURED)."
  },
  [AiAnswerReason.MarketplaceCompliance]: {
    title: "Pazaryeri uyum kapısına takıldı",
    detail:
      "MARKETPLACE_COMPLIANCE_GATE cevabı blokladı; cevap gönderilmedi, devredildi. İhlal kodları aşağıda."
  },
  [AiAnswerReason.PublicReplyOff]: {
    title: "Yorumlara açık cevap kapalı",
    detail:
      "Yüzey COMMENT ve publicReplyAllowed = false. Metin varsa DM olarak gitti, yoksa hiçbir şey gitmedi."
  },
  [AiAnswerReason.CommentDeleted]: {
    title: "Skill yorumu sildi",
    detail: "Skill DELETE_COMMENT çalıştırdı; yorumun altına cevap yazılmadı."
  },
  [AiAnswerReason.HandoffDisabledLine]: {
    title: "Devir kapalı — tenant'ın satırı gitti",
    detail:
      "Ajan cevap yazmadı ve kanalda devir kapalı; profilin handoff-disabled mesajı gönderildi (code SEND)."
  },
  [AiAnswerReason.QuotaExhausted]: {
    title: "Kota / bakiye yetersiz",
    detail:
      "GUARD_STAGE blocked = INSUFFICIENT_AI_QUOTA_OR_BALANCE; ajan hiç çalışmadı."
  },
  [AiAnswerReason.BudgetExceeded]: {
    title: "Ajan bütçesi doldu",
    detail:
      "AGENT_OUTPUT FAILED — max_turns veya timeout. failTurn devir mesajıyla kapattı, faturalanmadı."
  },
  [AiAnswerReason.UnexpectedError]: {
    title: "Beklenmeyen hata",
    detail:
      "Ajan ya da hazırlık hata verdi (AGENT_OUTPUT FAILED veya run FAILED). Hata metni run.error'da."
  },
  [AiAnswerReason.Superseded]: {
    title: "Müşteri yeniden yazdı",
    detail:
      "DELIVERY_GATE withheld / because = SUPERSEDED: cevap bayatladı, grup yeni mesajla yeniden koşuldu."
  },
  [AiAnswerReason.HumanTookOver]: {
    title: "Temsilci devraldı",
    detail:
      "DELIVERY_GATE withheld / because = HUMAN_TOOK_OVER: cevap hazırken görüşmeye bir kişi katıldı."
  },
  [AiAnswerReason.SurfaceNotSupported]: {
    title: "Yüzey desteklenmiyor",
    detail: "run.error = V2_SURFACE_NOT_SUPPORTED; ajan çalışmadı."
  },
  [AiAnswerReason.NoEligibleAiRole]: {
    title: "Uygun AI rolü yok",
    detail:
      "run.error = NO_ELIGIBLE_AI_ROLE: profil pasif, kanal rolün kanallarında değil ya da çalışma saati dışı."
  }
}

export const SUGGESTION_LABELS: Record<AiAnswerSuggestion, string> = {
  [AiAnswerSuggestion.AddKnowledge]: "Eksik bilgiyi bilgi kaynağına eklemeli",
  [AiAnswerSuggestion.ReviewKnowledgeSuggestions]:
    "Soru AI önerilerine düştü — oradan kaynağa çevrilebilir",
  [AiAnswerSuggestion.ReviewCitedKnowledge]:
    "Referans alınan bilgi kaynağı kurala aykırı içerik taşıyor",
  [AiAnswerSuggestion.ReviewGuidance]:
    "Kararı veren rehber kuralı gözden geçirilmeli",
  [AiAnswerSuggestion.AddGuidance]: "Bu durum için rehber kuralı yazılmalı",
  [AiAnswerSuggestion.AddSpamGuidance]: "SPAM kategorisinde kural eklenmeli",
  [AiAnswerSuggestion.ReviewSkill]: "Çalışan skill gözden geçirilmeli",
  [AiAnswerSuggestion.ReviewAiProfile]:
    "AI profil ayarları (cevap uzunluğu vb.) gözden geçirilmeli",
  [AiAnswerSuggestion.ReviewCommentSettings]:
    "Yorum kanalı ayarında açık cevap kapalı",
  [AiAnswerSuggestion.ReviewMarketplaceSettings]:
    "İlan mağaza ürünüyle eşleşmedi (marketplace_unresolved)",
  [AiAnswerSuggestion.ReviewAiRoles]:
    "AI rollerinin kanal / çalışma saati ayarları kontrol edilmeli",
  [AiAnswerSuggestion.TopUpBalance]: "Tenant'ın bakiyesi / paketi yetersiz",
  [AiAnswerSuggestion.ReplyManually]: "Mesaj cevapsız kalmış olabilir",
  [AiAnswerSuggestion.ContactSupport]: "Hata — run'ı /ai-debug ile incele"
}

export const COMPLIANCE_LABELS: Record<string, string> = {
  EXTERNAL_REDIRECT: "Link / pazaryeri dışına yönlendirme",
  CONTACT_DETAILS: "İletişim bilgisi",
  UNGROUNDED_NUMBER: "Kanıtsız rakam",
  ABSOLUTE_PROMISE: "Kesin taahhüt",
  PROMOTIONAL: "Reklam dili",
  HEALTH_CLAIM: "Sağlık iddiası",
  LEGAL_CLAIM: "Hukuki kesin hüküm",
  INAPPROPRIATE: "Uygunsuz ifade",
  PERSONAL_DATA: "Kişisel veri",
  LENGTH_LIMIT: "Uzunluk sınırı"
}

export const SOURCE_TYPE_LABELS: Record<string, string> = {
  KNOWLEDGE_SOURCE: "Bilgi kaynağı",
  COMMERCE_PRODUCT: "Ürün",
  ACTION: "Action",
  ACTION_REQUEST: "Action",
  SKILL: "Skill",
  GUIDANCE: "Rehber",
  MARKETPLACE_REQUEST: "Pazaryeri",
  IMAGE: "Görsel",
  AI_IMAGE_RECOGNITION: "Görsel",
  POST_DETAIL: "Gönderi",
  INSTRUCTION: "Talimat",
  AI_PROFILE: "AI profili"
}

/** The step the explanation read its verdict from, so the reader can open the raw payload. */
export function decisiveStep(
  steps: AiMessageSteps[] | undefined,
  reason: AiAnswerReason | null | undefined
): AiMessageSteps | undefined {
  const name = (() => {
    switch (reason) {
      case AiAnswerReason.QuotaExhausted:
        return "GUARD_STAGE"
      case AiAnswerReason.BudgetExceeded:
      case AiAnswerReason.UnexpectedError:
        return "AGENT_OUTPUT"
      case AiAnswerReason.MarketplaceCompliance:
        return "MARKETPLACE_COMPLIANCE_GATE"
      default:
        return "DELIVERY_GATE"
    }
  })()
  const matches = (steps ?? []).filter((step) => String(step.step) === name)
  return matches[matches.length - 1]
}
